import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import styles from "./FormTemplates.module.css";
import { FormFieldDef, FormFilledValues } from "../../api/models";

interface FormReviewEditorProps {
    fields: FormFieldDef[];
    values: FormFilledValues;
    initialValues: FormFilledValues;
    onChange: (next: FormFilledValues) => void;
}

const hasContent = (value: unknown): boolean => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.some(hasContent);
    if (typeof value === "object") return Object.values(value as Record<string, unknown>).some(hasContent);
    return true;
};

const asText = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === "";

const isPlainDate = (value: unknown): boolean => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

// Keeps raw text locally so typed newlines survive the round-trip through the
// filtered array value (a controlled textarea would erase them on re-render).
const ArrayTextEditor = ({
    value,
    placeholder,
    onValueChange
}: {
    value: unknown;
    placeholder: string;
    onValueChange: (next: unknown) => void;
}) => {
    const [text, setText] = useState(() => (Array.isArray(value) ? value.map(asText).join("\n") : asText(value)));
    return (
        <textarea
            className={styles.textarea}
            value={text}
            placeholder={placeholder}
            onChange={event => {
                setText(event.target.value);
                const lines = event.target.value.split("\n").filter(line => line.trim() !== "");
                onValueChange(lines.length > 0 ? lines : null);
            }}
        />
    );
};

interface FieldEditorProps {
    field: FormFieldDef;
    value: unknown;
    initialValue: unknown;
    onValueChange: (next: unknown) => void;
}

const FieldEditor = ({ field, value, initialValue, onValueChange }: FieldEditorProps) => {
    const { t } = useTranslation();
    const aiFilled = useMemo(() => hasContent(initialValue), [initialValue]);
    const wrapperClass = aiFilled ? styles.reviewFieldAiFilled : styles.reviewField;

    // Every value the reviewer approves must be visible: whenever the typed
    // control cannot represent the actual value, fall back to free text.
    const renderTextControl = () => (
        <textarea
            className={styles.textarea}
            value={asText(value)}
            onChange={event => onValueChange(event.target.value === "" ? null : event.target.value)}
        />
    );

    const renderRowsTable = (columnNames: string[]) => {
        const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
        const setRows = (nextRows: Record<string, unknown>[]) => onValueChange(nextRows.length > 0 ? nextRows : null);
        return (
            <div className={styles.tableWrap}>
                <table className={styles.reviewTable}>
                    <thead>
                        <tr>
                            {columnNames.map(name => (
                                <th key={name}>{name}</th>
                            ))}
                            <th aria-hidden="true" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {columnNames.map(name => (
                                    <td key={name}>
                                        <input
                                            className={styles.tableInput}
                                            value={asText(row?.[name])}
                                            onChange={event => {
                                                const nextRows = rows.map((existing, index) =>
                                                    index === rowIndex ? { ...existing, [name]: event.target.value } : existing
                                                );
                                                setRows(nextRows);
                                            }}
                                        />
                                    </td>
                                ))}
                                <td>
                                    <button
                                        type="button"
                                        className={styles.dangerButton}
                                        onClick={() => setRows(rows.filter((_, index) => index !== rowIndex))}
                                        aria-label={t("forms.tableRemoveRow")}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => setRows([...rows, Object.fromEntries(columnNames.map(name => [name, ""]))])}
                >
                    <Plus size={14} />
                    {t("forms.tableAddRow")}
                </button>
            </div>
        );
    };

    const rowsAreObjects = (candidate: unknown): boolean =>
        !Array.isArray(candidate) ||
        candidate.every(row => row !== null && typeof row === "object" && !Array.isArray(row));

    const renderControl = () => {
        if (field.type === "object") {
            const objectValue = (value && typeof value === "object" && !Array.isArray(value) ? value : {}) as FormFilledValues;
            const objectInitial = (initialValue && typeof initialValue === "object" && !Array.isArray(initialValue)
                ? initialValue
                : {}) as FormFilledValues;
            return (
                <div className={styles.reviewGroup}>
                    {(field.properties ?? []).map(child => (
                        <FieldEditor
                            key={child.field_name}
                            field={child}
                            value={objectValue[child.field_name]}
                            initialValue={objectInitial[child.field_name]}
                            onValueChange={next => onValueChange({ ...objectValue, [child.field_name]: next })}
                        />
                    ))}
                </div>
            );
        }

        if (field.type === "table") {
            if (!isEmptyValue(value) && !rowsAreObjects(value)) return renderTextControl();
            return renderRowsTable((field.table_columns ?? []).map(column => column.column_name));
        }

        if (field.type === "array") {
            if (field.properties && field.properties.length > 0) {
                if (!isEmptyValue(value) && !rowsAreObjects(value)) return renderTextControl();
                return renderRowsTable(field.properties.map(property => property.field_name));
            }
            return <ArrayTextEditor value={value} placeholder={t("forms.arrayPlaceholder")} onValueChange={onValueChange} />;
        }

        if (field.type === "boolean") {
            if (!isEmptyValue(value) && value !== true && value !== false) return renderTextControl();
            const selected = value === true ? "yes" : value === false ? "no" : "";
            return (
                <select
                    className={styles.select}
                    value={selected}
                    onChange={event => {
                        const next = event.target.value;
                        onValueChange(next === "yes" ? true : next === "no" ? false : null);
                    }}
                >
                    <option value="">{t("forms.notSpecified")}</option>
                    <option value="yes">{t("forms.yes")}</option>
                    <option value="no">{t("forms.no")}</option>
                </select>
            );
        }

        if (field.enum_values && field.enum_values.length > 0) {
            if (!isEmptyValue(value) && (typeof value !== "string" || !field.enum_values.includes(value))) {
                return renderTextControl();
            }
            const selected = typeof value === "string" ? value : "";
            return (
                <select
                    className={styles.select}
                    value={selected}
                    onChange={event => onValueChange(event.target.value || null)}
                >
                    <option value="">{t("forms.notSpecified")}</option>
                    {field.enum_values.map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            );
        }

        if (field.type === "date") {
            if (!isEmptyValue(value) && !isPlainDate(value)) return renderTextControl();
            return (
                <input
                    type="date"
                    className={styles.textInput}
                    value={asText(value)}
                    onChange={event => onValueChange(event.target.value || null)}
                />
            );
        }

        return renderTextControl();
    };

    return (
        <div className={wrapperClass}>
            <span className={styles.label}>{field.field_name}</span>
            {field.description && <span className={styles.reviewDescription}>{field.description}</span>}
            {renderControl()}
        </div>
    );
};

export const FormReviewEditor = ({ fields, values, initialValues, onChange }: FormReviewEditorProps) => {
    return (
        <div className={styles.section}>
            {fields.map(field => (
                <FieldEditor
                    key={field.field_name}
                    field={field}
                    value={values[field.field_name]}
                    initialValue={initialValues[field.field_name]}
                    onValueChange={next => onChange({ ...values, [field.field_name]: next })}
                />
            ))}
        </div>
    );
};
